<template>
  <div :class="classObject">
    <div class="wrapper">
      <div class="alertbox__wrapper">
        <div class="alertbox__icon">
          <icon :name="iconName" />
        </div>

        <div class="alertbox__text">
          <h3 v-if="title">
            {{ title }}
          </h3>
          <p>
            <slot />
          </p>
        </div>

        <div v-if="closeButton" class="alertbox__icon-close">
          <icon-btn class="is-right" icon="close" :size="50" @click="closeAlert" />
        </div>
      </div>

      <div v-if="hasButtonSlot" class="alertbox__button">
        <slot name="button" />
      </div>
    </div>
  </div>
</template>

<script>
import icon from "../icon/icon";
import iconBtn from "../button/iconButton";
import "@smartdesign/core/css/components/alertbox.css";

export default {
  components: {
    icon,
    iconBtn,
  },

  props: {
    /**
     * alertbox title
     */
    title: {
      required: false,
      type: String,
      default: undefined,
    },

    /**
     * alertbox visible state
     */
    show: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * alertbox type `error` `warning` `success` `info`
     */
    type: {
      required: false,
      type: String,
      default: "error",
      validator: (val) => {
        return ["error", "warning", "success", "info"].indexOf(val) !== -1;
      },
    },

    /**
     * important variant
     */
    important: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * add a close button to alertbox
     */
    closeButton: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * set alertbox--important fixed at the top of the page
     */
    isFixed: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * duration in `ms`, until the alertbox is closed (must be greater 0)
     */
    duration: {
      required: false,
      type: Number,
      default: undefined,
      validator: (val) => {
        return val > 0;
      },
    },
  },

  data() {
    return {
      isShown: this.show,
    };
  },

  computed: {
    classObject() {
      return {
        alertbox: true,
        "alertbox--standard": !this.important,
        "alertbox--important": this.important,
        "alertbox--is-shown": this.isShown,
        "alertbox--is-hidden": !this.isShown,
        "alertbox--error": this.type === "error",
        "alertbox--warning": this.type === "warning",
        "alertbox--success": this.type === "success",
        "alertbox--info": this.type === "info",
        "alertbox--is-fixed": this.isFixed && this.important,
      };
    },

    iconName() {
      let iconName = "exclamation_triangle";
      switch (this.type) {
        case "error":
          iconName = "exclamation_triangle";
          break;
        case "warning":
          iconName = "exclamation";
          break;
        case "success":
          iconName = "check";
          break;
        case "info":
          iconName = "info";
          break;
      }
      return iconName;
    },

    hasButtonSlot() {
      return !!this.$slots["button"];
    },
  },

  watch: {
    show(newVal) {
      if (!newVal) {
        this.closeAlert();
      } else {
        this.isShown = newVal;
        new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
      }

      if (this.duration !== undefined && this.isShown) {
        this.setCloseDuration();
      }
    },
  },

  methods: {
    closeAlert() {
      if (!this.isShown) {
        return;
      }

      if (this.important) {
        this.$el.style.height = this.$el.offsetHeight + "px";

        setTimeout(() => {
          this.isShown = false;
          this.$el.style.height = "";
        }, 200);
      } else {
        this.isShown = false;
      }

      // Fire on close alert
      // @arg The argument is a boolean value representing `false`
      this.$emit("close", false);
    },

    setCloseDuration() {
      setTimeout(() => {
        this.closeAlert();
      }, this.duration);
    },
  },
};
</script>

<style>
.alertbox.alertbox--standard.alertbox--error .alertbox__icon svg {
  color: var(--color-red-300);
}
.alertbox.alertbox--standard.alertbox--warning .alertbox__icon svg {
  color: var(--color-primary-300);
}
.alertbox.alertbox--standard.alertbox--success .alertbox__icon svg {
  color: var(--color-green-300);
}
.alertbox.alertbox--standard.alertbox--info .alertbox__icon svg {
  color: var(--color-secondary-300);
}
</style>
